/**
 * White-label theme presets — typed metadata for the picker UI.
 *
 * The actual CSS token values for each preset live in `apps/frontend/src/globals.css`
 * under `:root[data-theme-preset="<id>"] { ... }` selector blocks. This file only
 * carries the picker-facing metadata (label, swatches, description).
 *
 * The canonical id lists + type guards live in `@digital-twins/shared-types`
 * (`packages/shared-types/src/white-label.ts`) so that the backend DTOs can
 * allowlist-validate them with the same source of truth — both runtimes accept
 * exactly the values the picker offers.
 *
 * ⚠️  If you add or rename a preset id or font id, you MUST ALSO update the
 *     `KNOWN_PRESETS` / `KNOWN_FONTS` inline allowlists in
 *     `apps/frontend/index.html` (pre-React boot script — cannot import TS,
 *     intentionally duplicated).
 *
 * CSS variable ownership manifest (see also packages/deck-ui/src/foundations/theme/applyThemeBrandTokens.ts):
 * - globals.css `:root` / `.dark`            — base Fairgen defaults (lowest specificity)
 * - globals.css `:root[data-theme-preset]`   — preset overrides for shell-surface tokens
 *   includes: --background, --foreground, --card, --card-foreground,
 *             --card-inset, --card-border, --surface, --muted, --muted-foreground,
 *             --border, --input, --accent, --accent-foreground, --sidebar-*, --brand-panel-bg,
 *             --brand-preview-bg, --tooltip, --tooltip-foreground, --popover, --popover-foreground,
 *             --toggle-inactive, --share-action, --share-action-foreground,
 *             --share-gate-icon, --share-gate-icon-background, --radius,
 *             --twin-chat-bg, --twin-chat-surface, --twin-chat-bubble, --twin-chat-fg,
 *             --twin-chat-fg-muted, --twin-chat-muted, --twin-chat-accent  (chat room surfaces)
 *             --report-bg, --report-card, --report-surface, --report-heading,
 *             --report-muted  (report page surfaces and typography)
 * - `:root[data-heading-font]` / `:root[data-font]` selectors (in globals.css)  — write `--font-display` / `--font-geist-sans`. These cascade into the deck-ui `--ds-font-display` / `--ds-font-sans` bridge tokens so slide fonts follow the active brand font automatically.
 * - ThemeProvider.tsx `style.setProperty`    — per-org `themeOverrides` (inline, wins over preset CSS)
 * - `applyThemeBrandTokens()` `style.setProperty` — sparse brand+deck deltas (inline, highest priority).
 *   Writes deck tokens plus brand-coupled product tokens for non-default white-label brands:
 *   --brand-*, --slide-*, --ds-*, --primary, --cta, --ring, share actions,
 *   sidebar brand affordances, and explicit app chart palettes only.
 *   Caller (BrandProvider) skips this function for Fairgen-default and non-white-label product chrome.
 *   `resetThemeBrandTokens()` also removes legacy inline foundation tokens, reverting to preset CSS / base :root values.
 */

export {
  THEME_PRESET_IDS,
  THEME_FONT_IDS,
  THEME_HEADING_FONT_IDS,
  CUSTOMIZABLE_TOKEN_KEYS,
  isThemePresetId,
  isThemeFontId,
  isThemeHeadingFontId,
  isCustomizableTokenKey,
  isThemeOverrideKey,
  sanitizeThemeOverrides,
} from '@digital-twins/shared-types'
export type {
  ThemePresetId,
  ThemeFontId,
  ThemeHeadingFontId,
  CustomizableTokenKey,
  CustomizableTokenModeKey,
} from '@digital-twins/shared-types'

import type { BrandChartPaletteId, ThemePresetId, ThemeFontId, ThemeHeadingFontId } from '@digital-twins/shared-types'

/**
 * Frontend-only sentinel representing the locked Fairgen default theme.
 *
 * This value is NEVER stored in the database — the backend represents
 * "Fairgen default" as `themePresetId: null`. On the frontend this sentinel
 * is used only inside the preset picker UI so Fairgen has an explicit,
 * selectable tile. When the user picks it, `ThemeProvider` translates it to
 * `setPresetId(null)` + clears overrides + clears font + resets brand primary.
 */
export const FAIRGEN_PRESET_SENTINEL = 'fairgen' as const
export type FairgenSentinelId = typeof FAIRGEN_PRESET_SENTINEL

/**
 * Frontend-only sentinel representing a custom auto-detected or manually-tuned theme.
 *
 * This value is NEVER stored in the database. The tile is shown in the picker whenever
 * the org has active `themeOverrides` (populated by the enriched brand lookup scraper or
 * manually via the Customize panel). Clicking it is a no-op — it just confirms the user
 * is aware they are on a custom theme. The tile disappears only when the user explicitly
 * resets to one of the named presets (which clears overrides).
 */
export const CUSTOM_PRESET_SENTINEL = 'custom' as const
export type CustomSentinelId = typeof CUSTOM_PRESET_SENTINEL

/**
 * Union of all preset IDs shown in the picker UI (sentinels + real presets).
 * Use `ThemePresetId` when you need a value that can be persisted to the backend.
 * Use `PickerPresetId` when rendering the preset grid in BrandTab.
 */
export type PickerPresetId = FairgenSentinelId | CustomSentinelId | ThemePresetId

export interface ThemePresetSwatches {
  /** Background swatch (page surface). */
  background: string
  /** Card / surface swatch. */
  card: string
  /** Sidebar swatch. */
  sidebar: string
  /** Accent swatch for the picker chip. */
  accent: string
  /**
   * Border swatch — mirrors the preset's `--border` CSS variable.
   * Used by the Advanced Customize panel as the default value of the
   * `--border` color input when no override is set. Keep in sync with
   * `:root[data-theme-preset="<id>"] { --border: ... }` in globals.css.
   */
  border: string
}

export interface ThemePreset {
  id: PickerPresetId
  label: string
  description: string
  /**
   * When true this tile represents the locked Fairgen default. Selecting it
   * resets presetId to null, clears all overrides + font, and removes the
   * inline brand-primary write. The Customize panel is hidden entirely.
   */
  isFairgenDefault?: true
  /**
   * When true this tile represents the active custom/auto-detected theme.
   * It is only shown when the org has active themeOverrides. Selecting it
   * is a no-op — it just keeps the Customize panel open.
   */
  isCustomTheme?: true
  /** 4-swatch row shown next to the preset name in the picker UI. */
  swatches: {
    light: ThemePresetSwatches
    dark: ThemePresetSwatches
  }
  /**
   * Default heading font to stage when this preset is selected.
   * `null` = no suggestion (leave whatever the user already has).
   * Only used in the preset-click handler in BrandTab.
   */
  defaultHeadingFont?: ThemeHeadingFontId | null
  /**
   * Default body font to stage when this preset is selected.
   * `null` = no suggestion (leave whatever the user already has).
   */
  defaultBodyFont?: ThemeFontId | null
  /** Default chart palette selected when the preset is applied. */
  defaultChartPaletteId?: BrandChartPaletteId | null
}

/**
 * Canonical preset list. Swatch hexes mirror the values in `globals.css` so the
 * picker tile and the actual applied theme stay in sync.
 *
 * Keep this list byte-stable across renders — no entropy, no per-call mutation.
 */
export const THEME_PRESETS: Readonly<Record<PickerPresetId, ThemePreset>> = {
  /** Fairgen default — locked, not customizable, maps to presetId: null in DB. */
  fairgen: {

    id: 'fairgen',
    label: 'Fairgen',
    description: 'The Fairgen default. Dark teal platform identity.',
    isFairgenDefault: true,
    swatches: {
      light: { background: '#F8FCFA', card: '#EBF4F0', sidebar: '#F2F8F5', accent: '#0F4A3D', border: '#D8E4DF' },
      dark:  { background: '#060E0C', card: '#0C1A16', sidebar: '#091310', accent: '#A4E4CD', border: '#192A24' },
    },
    defaultHeadingFont: 'sumana',
    defaultBodyFont: null,
    defaultChartPaletteId: 'forest',
  },
  slate: {
    id: 'slate',
    label: 'Slate',
    description: 'Cool neutral grey with blue undertones. Clean enterprise look.',
    swatches: {
      light: { background: '#F8FAFC', card: '#FFFFFF', sidebar: '#F1F5F9', accent: '#475569', border: '#E1E7EF' },
      dark: { background: '#0F172A', card: '#1E293B', sidebar: '#0B1220', accent: '#94A3B8', border: '#283548' },
    },
    defaultHeadingFont: 'inter',
    defaultBodyFont: 'inter',
    defaultChartPaletteId: 'slate',
  },
  midnight: {
    id: 'midnight',
    label: 'Midnight',
    description: 'Deep indigo and navy. Sophisticated and premium.',
    swatches: {
      light: { background: '#FAFAFD', card: '#FFFFFF', sidebar: '#F4F4FB', accent: '#4338CA', border: '#DEE0ED' },
      dark: { background: '#0B0F2E', card: '#15193D', sidebar: '#080B22', accent: '#A5B4FC', border: '#2B2F50' },
    },
    defaultHeadingFont: 'ibm-plex-sans',
    defaultBodyFont: 'ibm-plex-sans',
    defaultChartPaletteId: 'twilight',
  },
  paper: {
    id: 'paper',
    label: 'Paper',
    description: 'Warm off-white with editorial typography energy.',
    swatches: {
      light: { background: '#FBF8F2', card: '#FFFFFF', sidebar: '#F5F0E5', accent: '#8B6F3D', border: '#E7E2DA' },
      dark: { background: '#1B1815', card: '#26221C', sidebar: '#141210', accent: '#E5D4A8', border: '#3E3832' },
    },
    defaultHeadingFont: 'source-serif-4',
    defaultBodyFont: 'inter',
    defaultChartPaletteId: 'earth',
  },
  graphite: {
    id: 'graphite',
    label: 'Basic',
    description: 'Universal neutral fallback. White in light mode, near-black graphite in dark mode.',
    swatches: {
      light: { background: '#FFFFFF', card: '#F7F7F7', sidebar: '#FFFFFF', accent: '#525252', border: '#E5E5E5' },
      dark: { background: '#111111', card: '#1A1A1A', sidebar: '#0B0B0B', accent: '#D4D4D4', border: '#2A2A2A' },
    },
    defaultHeadingFont: 'inter',
    defaultBodyFont: 'inter',
    defaultChartPaletteId: 'slate',
  },
  /**
   * Custom — auto-detected or manually-tuned theme. Present in the picker whenever
   * the org has active themeOverrides. Swatches are placeholder; the actual colors
   * are read live from themeOverrides in the picker tile. Never stored in the DB.
   */
  custom: {
    id: 'custom',
    label: 'Custom',
    description: 'Auto-detected from your website or manually fine-tuned.',
    isCustomTheme: true,
    swatches: {
      light: { background: '#F8F8F8', card: '#FFFFFF', sidebar: '#F0F0F0', accent: '#888888', border: '#E0E0E0' },
      dark: { background: '#181818', card: '#242424', sidebar: '#101010', accent: '#888888', border: '#333333' },
    },
    defaultHeadingFont: null,
    defaultBodyFont: null,
    defaultChartPaletteId: null,
  },
}

/**
 * Border hex defaults for the "no preset selected" Fairgen baseline — mirror
 * the `--border` value from `globals.css :root` (light: `0 0% 88.6%`) and
 * `:root.dark` (dark: `171 26% 15%` / `#1C302D`). Used by `BrandTab`'s Advanced Customize
 * panel when `stagedPresetId` is null.
 */
export const FAIRGEN_BASE_BORDER = {
  light: '#E2E2E2',
  dark: '#1C302D',
} as const

/** Font registry — typed metadata for the font picker. Actual @font-face + CSS variable bindings live in `apps/frontend/src/styles/fonts.css`. */

export interface ThemeFont {
  id: ThemeFontId
  label: string
  /** Inline `font-family` string used to render the preview tile in the picker. */
  previewFamily: string
}

export const THEME_FONTS: Readonly<Record<ThemeFontId, ThemeFont>> = {
  inter: {
    id: 'inter',
    label: 'Inter',
    previewFamily: '"Inter", system-ui, sans-serif',
  },
  'ibm-plex-sans': {
    id: 'ibm-plex-sans',
    label: 'IBM Plex Sans',
    previewFamily: '"IBM Plex Sans", system-ui, sans-serif',
  },
  'source-serif-4': {
    id: 'source-serif-4',
    label: 'Source Serif 4',
    previewFamily: '"Source Serif 4", Georgia, serif',
  },
  'dm-sans': {
    id: 'dm-sans',
    label: 'DM Sans',
    previewFamily: '"DM Sans", system-ui, sans-serif',
  },
  roboto: {
    id: 'roboto',
    label: 'Roboto',
    previewFamily: '"Roboto", system-ui, sans-serif',
  },
}

/**
 * Preload paths for the active-weight (400) woff2 file of each font family.
 * Mirrors the woff2 assets in `apps/frontend/public/fonts/` so a `<link rel="preload">`
 * tag can avoid a flash of unstyled text (FOUT) when a white-label org sets a
 * non-default font. The 600-weight is loaded lazily on demand by the `@font-face`
 * declarations in `apps/frontend/src/styles/fonts.css`.
 */
export const FONT_PRELOAD_HREFS: Readonly<Record<ThemeFontId, string>> = {
  inter: '/fonts/inter-400.woff2',
  'ibm-plex-sans': '/fonts/ibm-plex-sans-400.woff2',
  'source-serif-4': '/fonts/source-serif-4-400.woff2',
  'dm-sans': '/fonts/dm-sans-400.woff2',
  roboto: '/fonts/roboto-400.woff2',
}

/** Heading font registry — typed metadata for the heading font picker. */

export interface ThemeHeadingFont {
  id: ThemeHeadingFontId
  label: string
  /** Inline `font-family` string used to render the preview tile in the picker. */
  previewFamily: string
  /** True for the Fairgen default (Sumana) — shown as the first tile, no DB value stored. */
  isFairgenDefault?: true
}

export const THEME_HEADING_FONTS: Readonly<Record<ThemeHeadingFontId, ThemeHeadingFont>> = {
  sumana: {
    id: 'sumana',
    label: 'Sumana',
    previewFamily: 'Sumana, Georgia, serif',
    isFairgenDefault: true,
  },
  inter: {
    id: 'inter',
    label: 'Inter',
    previewFamily: '"Inter", system-ui, sans-serif',
  },
  'ibm-plex-sans': {
    id: 'ibm-plex-sans',
    label: 'IBM Plex Sans',
    previewFamily: '"IBM Plex Sans", system-ui, sans-serif',
  },
  'playfair-display': {
    id: 'playfair-display',
    label: 'Playfair Display',
    previewFamily: '"Playfair Display", Georgia, serif',
  },
  'source-serif-4': {
    id: 'source-serif-4',
    label: 'Source Serif 4',
    previewFamily: '"Source Serif 4", Georgia, serif',
  },
  montserrat: {
    id: 'montserrat',
    label: 'Montserrat',
    previewFamily: '"Montserrat", system-ui, sans-serif',
  },
}

